import React from "react";
import { Box, Card, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import { useTranslation } from "react-i18next";

// Filled pill trend indicator (no border) - matches the reference "hero KPI card"
// look, distinct from the outlined DeltaChip used elsewhere in the analytics UI.
function TrendPill({ changePercent }) {
  const { t } = useTranslation("boShared");
  if (changePercent === null || changePercent === undefined) {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{
          display: "inline-flex",
          px: 1.5,
          py: 0.6,
          borderRadius: 999,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
          color: (theme) => theme.palette.primary.dark,
        }}
      >
        <AutoAwesomeRoundedIcon sx={{ fontSize: 15 }} />
        <Typography variant="body2" fontWeight={800}>
          {t("new")}
        </Typography>
      </Stack>
    );
  }
  if (changePercent === 0) return null;

  const isUp = changePercent > 0;
  const color = isUp ? "success" : "error";
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{
        display: "inline-flex",
        px: 1.5,
        py: 0.6,
        borderRadius: 999,
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.12),
        color: (theme) => theme.palette[color].dark ?? theme.palette[color].main,
      }}
    >
      {isUp ? <ArrowUpwardRoundedIcon sx={{ fontSize: 15 }} /> : <ArrowDownwardRoundedIcon sx={{ fontSize: 15 }} />}
      <Typography variant="body2" fontWeight={800}>
        {Math.abs(changePercent)}%
      </Typography>
    </Stack>
  );
}

// KPI "hero" card for the business-owner dashboard overview row (Revenue / Orders /
// Visitors / Conversion). Deliberately its own component rather than a restyle of
// the shared analyticsShared StatCard, which is also used by Admin/Delivery-company
// screens that never asked for this heavier, illustrative look.
export default function StatCard({ icon: Icon, label, subtitle, value, changePercent, color = "primary", onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        position: "relative",
        height: "100%",
        borderRadius: "22px",
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: onClick ? "translateY(-3px)" : "translateY(-1px)",
          boxShadow: (theme) => `0 18px 34px ${alpha(theme.palette[color].main, 0.2)}`,
        },
      }}
    >
      {/* soft decorative blob, bottom-right */}
      <Box
        sx={{
          position: "absolute",
          bottom: -46,
          right: -36,
          width: 190,
          height: 150,
          borderRadius: "50%",
          bgcolor: (theme) => alpha(theme.palette[color].light, 0.14),
          filter: "blur(2px)",
          pointerEvents: "none",
        }}
      />
      {/* concentric ring behind the hero icon */}
      <Box
        sx={{
          position: "absolute",
          top: 26,
          right: 22,
          width: 108,
          height: 108,
          borderRadius: "50%",
          border: (theme) => `1px solid ${alpha(theme.palette[color].main, 0.16)}`,
          display: { xs: "none", sm: "block" },
          pointerEvents: "none",
        }}
      />
      {/* dot-grid accent */}
      <Box
        sx={{
          position: "absolute",
          top: 20,
          right: 108,
          width: 46,
          height: 46,
          display: { xs: "none", md: "block" },
          backgroundImage: (theme) =>
            `radial-gradient(${alpha(theme.palette[color].main, 0.35)} 1.4px, transparent 1.4px)`,
          backgroundSize: "9px 9px",
          pointerEvents: "none",
        }}
      />

      <Box sx={{ position: "relative", p: 2.75, pt: 3.25 }}>
        {/* top accent pill bar */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 20,
            right: 20,
            height: 5,
            borderRadius: 999,
            background: (theme) => `linear-gradient(90deg, ${theme.palette[color].light}, ${theme.palette[color].dark})`,
          }}
        />

        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ pr: { xs: 0, sm: "84px" } }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "13px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (theme) => alpha(theme.palette[color].main, 0.12),
              color: (theme) => theme.palette[color].dark,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25 }}>
            <Typography
              variant="body2"
              fontWeight={800}
              noWrap
              sx={{ textTransform: "uppercase", letterSpacing: "0.08em", color: "text.primary" }}
            >
              {label}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>

        <Box sx={{ height: "1px", bgcolor: "divider", my: 2 }} />

        <Typography
          sx={{
            fontWeight: 800,
            letterSpacing: "-0.02em",
            fontSize: { xs: 28, sm: 32 },
            lineHeight: 1.15,
            color: "text.primary",
          }}
          noWrap
        >
          {value}
        </Typography>

        <Box sx={{ mt: 2, minHeight: 30 }}>
          <TrendPill changePercent={changePercent} />
        </Box>

        {/* hero circle icon */}
        <Box
          sx={{
            position: "absolute",
            top: 34,
            right: 34,
            width: 68,
            height: 68,
            borderRadius: "50%",
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            justifyContent: "center",
            background: (theme) => `linear-gradient(135deg, ${theme.palette[color].main}, ${theme.palette[color].dark})`,
            boxShadow: (theme) => `0 10px 22px ${alpha(theme.palette[color].main, 0.42)}`,
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: 28 }} />
        </Box>
      </Box>
    </Card>
  );
}
